import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminShell, { useAdminKey } from '../components/admin/AdminShell';

const BOOL_FLAGS = [
  { key: 'cis_accredited', label: 'CIS accredited' },
  { key: 'apple_distinguished_school', label: 'Apple Distinguished' },
  { key: 'fobisia_member', label: 'FOBISIA member' },
  { key: 'mun_host', label: 'MUN host' },
  { key: 'mun_active', label: 'MUN active' },
  { key: 'isqm_gold', label: 'ISQM Gold' },
];

const ATTR_NUMBER_FIELDS = [
  { key: 'exam_results_strength', label: 'Exam results strength (0-1)', step: 0.01 },
  { key: 'exam_results_strength_confidence', label: 'Exam strength confidence (0-1)', step: 0.01 },
  { key: 'pastoral_care', label: 'Pastoral care (0-1)', step: 0.01 },
  { key: 'pastoral_care_confidence', label: 'Pastoral confidence (0-1)', step: 0.01 },
  { key: 'cocurricular', label: 'Co-curricular (0-1)', step: 0.01 },
  { key: 'cocurricular_confidence', label: 'Co-curricular confidence (0-1)', step: 0.01 },
];

const ATTR_TEXT_FIELDS = [
  { key: 'exam_headline', label: 'Exam headline', long: true },
  { key: 'exam_benchmark', label: 'Exam benchmark' },
  { key: 'exam_year', label: 'Exam year' },
  { key: 'pastoral_care_display_text', label: 'Pastoral care display text', long: true },
];

const parseLinesInput = (val) =>
  val
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

const parseIntArrayInput = (val) =>
  val
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n));

const SchoolsBody = () => {
  const { adminKey } = useAdminKey();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name_asc');
  const [schools, setSchools] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const [tab, setTab] = useState('list'); // 'list' | 'audit'
  const [auditFilter, setAuditFilter] = useState(null); // {school_id, school_name} | null

  const openAuditForSchool = (school) => {
    setAuditFilter({ school_id: school.id, school_name: school.full_name });
    setTab('audit');
  };

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const url = new URL(`${API_URL}/api/admin/schools`);
      url.searchParams.set('admin_key', adminKey);
      if (q.trim()) url.searchParams.set('q', q.trim());
      if (sort && sort !== 'name_asc') url.searchParams.set('sort', sort);
      const res = await fetch(url.toString());
      if (!res.ok) {
        setListError(`Failed (${res.status}).`);
        setSchools([]);
      } else {
        const data = await res.json();
        setSchools(data.schools || []);
      }
    } catch {
      setListError('Network error.');
    }
    setListLoading(false);
  }, [API_URL, adminKey, q, sort]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openEditor = async (id) => {
    setEditingId(id);
    setDetail(null);
    setSaveMsg('');
    setDetailLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/schools/${id}?admin_key=${encodeURIComponent(adminKey)}`
      );
      if (!res.ok) {
        setSaveMsg(`Failed to load (${res.status}).`);
        setDetailLoading(false);
        return;
      }
      const data = await res.json();
      const school = data.school || {};
      const attrs = data.attributes || {};
      setDetail({
        school: {
          full_name: school.full_name || '',
          curriculum_framework: school.curriculum_framework || '',
          campus_locations: (school.campus_locations || []).join('\n'),
          year_groups_covered: (school.year_groups_covered || []).join(', '),
          annual_fees: JSON.stringify(school.annual_fees || {}, null, 2),
          unique_signal: school.unique_signal || '',
          active: school.active !== false,
          cis_accredited: !!school.cis_accredited,
          apple_distinguished_school: !!school.apple_distinguished_school,
          fobisia_member: !!school.fobisia_member,
          mun_host: !!school.mun_host,
          mun_active: !!school.mun_active,
          isqm_gold: !!school.isqm_gold,
          cobis_tier: school.cobis_tier || '',
        },
        attributes: {
          exam_headline: attrs.exam_headline || '',
          exam_benchmark: attrs.exam_benchmark || '',
          exam_year: attrs.exam_year || '',
          exam_results_strength: attrs.exam_results_strength ?? '',
          exam_results_strength_confidence: attrs.exam_results_strength_confidence ?? '',
          pastoral_care: attrs.pastoral_care ?? '',
          pastoral_care_confidence: attrs.pastoral_care_confidence ?? '',
          pastoral_care_display_text: attrs.pastoral_care_display_text || '',
          cocurricular: attrs.cocurricular ?? '',
          cocurricular_confidence: attrs.cocurricular_confidence ?? '',
        },
      });
    } catch {
      setSaveMsg('Network error loading school.');
    }
    setDetailLoading(false);
  };

  const closeEditor = () => {
    setEditingId(null);
    setDetail(null);
    setSaveMsg('');
  };

  const updateSchoolField = (key, value) =>
    setDetail((d) => ({ ...d, school: { ...d.school, [key]: value } }));
  const updateAttrField = (key, value) =>
    setDetail((d) => ({ ...d, attributes: { ...d.attributes, [key]: value } }));

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    setSaveMsg('');

    let parsedFees;
    try {
      parsedFees = detail.school.annual_fees.trim() ? JSON.parse(detail.school.annual_fees) : {};
    } catch {
      setSaveMsg('Annual fees is not valid JSON.');
      setSaving(false);
      return;
    }

    const schoolPatch = {
      full_name: detail.school.full_name,
      curriculum_framework: detail.school.curriculum_framework,
      campus_locations: parseLinesInput(detail.school.campus_locations),
      year_groups_covered: parseIntArrayInput(detail.school.year_groups_covered),
      annual_fees: parsedFees,
      unique_signal: detail.school.unique_signal,
      active: detail.school.active,
      cis_accredited: detail.school.cis_accredited,
      apple_distinguished_school: detail.school.apple_distinguished_school,
      fobisia_member: detail.school.fobisia_member,
      mun_host: detail.school.mun_host,
      mun_active: detail.school.mun_active,
      isqm_gold: detail.school.isqm_gold,
      cobis_tier: detail.school.cobis_tier,
    };

    const attrPatch = {};
    Object.entries(detail.attributes).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      if (ATTR_NUMBER_FIELDS.some((f) => f.key === k)) {
        const num = parseFloat(v);
        if (Number.isFinite(num)) attrPatch[k] = num;
      } else {
        attrPatch[k] = v;
      }
    });

    try {
      const res = await fetch(
        `${API_URL}/api/admin/schools/${editingId}?admin_key=${encodeURIComponent(adminKey)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ school: schoolPatch, attributes: attrPatch }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setSaveMsg(data.message || data.detail || `Save failed (${res.status}).`);
      } else {
        setSaveMsg('Saved.');
        fetchList();
      }
    } catch {
      setSaveMsg('Network error on save.');
    }
    setSaving(false);
  };

  const signalCount = useMemo(
    () =>
      schools.map((s) => ({
        id: s.id,
        verifiedFlags: BOOL_FLAGS.reduce((acc, f) => acc + (s[f.key] ? 1 : 0), 0),
      })),
    [schools]
  );
  const flagMap = Object.fromEntries(signalCount.map((x) => [x.id, x.verifiedFlags]));

  return (
    <div data-testid="admin-schools-body">
      <div className="mb-4 flex gap-2 border-b border-[#E8DED3]">
        {[
          { id: 'list', label: 'Schools' },
          { id: 'audit', label: 'Recent changes' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${
              tab === t.id
                ? 'border-[#E85D35] text-[#E85D35]'
                : 'border-transparent text-[#6B6560] hover:text-[#2C2823]'
            }`}
            data-testid={`admin-schools-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'audit' ? (
        <AuditLogPanel
          adminKey={adminKey}
          apiUrl={API_URL}
          filter={auditFilter}
          onClearFilter={() => setAuditFilter(null)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchList()}
          placeholder="Search by name or campus…"
          className="flex-1 min-w-[240px] border border-[#E8DED3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85D35]"
          data-testid="admin-schools-search"
        />
        <button
          onClick={fetchList}
          disabled={listLoading}
          className="bg-[#2C2823] hover:bg-[#1A1816] disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          data-testid="admin-schools-search-btn"
        >
          {listLoading ? 'Searching…' : 'Search'}
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="border border-[#E8DED3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85D35] bg-white"
          data-testid="admin-schools-sort"
          title="Sort schools"
        >
          <option value="name_asc">Sort: A → Z</option>
          <option value="enrichment_asc">Least enriched first</option>
          <option value="enrichment_desc">Most enriched first</option>
        </select>
        <a
          href={`${API_URL}/api/admin/schools/enrichment-audit.csv?admin_key=${encodeURIComponent(adminKey)}`}
          download="schools_enrichment_audit.csv"
          className="px-3 py-2 rounded-lg text-sm font-semibold border border-[#E8DED3] text-[#2C2823] hover:bg-[#FFF5EB]"
          data-testid="admin-schools-csv-export"
          title="Download CSV: id, full_name, campus, enrichment_score, missing_fields"
        >
          ↓ CSV
        </a>
        <span className="text-xs text-[#9B8F88]">
          {schools.length} {schools.length === 1 ? 'school' : 'schools'}
        </span>
      </div>

      {listError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {listError}
        </div>
      )}

      <div className="bg-white border border-[#E8DED3] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF5EB] text-left text-[#2C2823]">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Campus</th>
              <th className="px-4 py-2">Curriculum</th>
              <th className="px-4 py-2 w-24 text-center">Score</th>
              <th className="px-4 py-2 w-20 text-center">Active</th>
              <th className="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {schools.map((s) => (
              <tr
                key={s.id}
                className="border-t border-[#E8DED3] hover:bg-[#FEFDF8]"
                data-testid={`school-row-${s.id}`}
              >
                <td className="px-4 py-2 font-semibold text-[#2C2823]">{s.full_name}</td>
                <td className="px-4 py-2 text-[#6B6560]">
                  {(s.campus_locations || []).join(', ') || '—'}
                </td>
                <td className="px-4 py-2 text-[#6B6560] text-xs">
                  {s.curriculum_framework || '—'}
                </td>
                <td className="px-4 py-2 text-center">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      (s.enrichment_score ?? 0) >= 4
                        ? 'bg-emerald-100 text-emerald-800'
                        : (s.enrichment_score ?? 0) >= 1
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                    title="Enrichment score: unique_signal + 6 verified flags + pastoral_care_display_text + exam_headline (max 9)"
                    data-testid={`enrichment-score-${s.id}`}
                  >
                    {s.enrichment_score ?? flagMap[s.id] ?? 0}/9
                  </span>
                </td>
                <td className="px-4 py-2 text-center">
                  {s.active === false ? (
                    <span className="text-xs text-red-600 font-semibold">off</span>
                  ) : (
                    <span className="text-xs text-emerald-700 font-semibold">on</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => openAuditForSchool(s)}
                      className="text-xs text-[#6B6560] hover:text-[#2C2823] hover:underline"
                      data-testid={`school-audit-link-${s.id}`}
                      title="Open this school's edit history"
                    >
                      History
                    </button>
                    <button
                      onClick={() => openEditor(s.id)}
                      className="text-xs font-semibold text-[#E85D35] hover:underline"
                      data-testid={`edit-school-${s.id}`}
                    >
                      Edit →
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {schools.length === 0 && !listLoading && (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-[#9B8F88]">
                  No schools match this search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      {editingId && (
        <EditorDrawer
          editingId={editingId}
          detail={detail}
          detailLoading={detailLoading}
          saving={saving}
          saveMsg={saveMsg}
          onClose={closeEditor}
          onSave={handleSave}
          onSchoolFieldChange={updateSchoolField}
          onAttrFieldChange={updateAttrField}
        />
      )}
    </div>
  );
};

const AuditLogPanel = ({ adminKey, apiUrl, filter, onClearFilter }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filterSchoolId = filter?.school_id || null;

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${apiUrl}/api/admin/schools/audit-log`);
      url.searchParams.set('admin_key', adminKey);
      url.searchParams.set('limit', '100');
      if (filterSchoolId) url.searchParams.set('school_id', filterSchoolId);
      const res = await fetch(url.toString());
      if (!res.ok) {
        setError(`Failed (${res.status}).`);
      } else {
        const data = await res.json();
        setEntries(data.entries || []);
      }
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, [apiUrl, adminKey, filterSchoolId]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  return (
    <div data-testid="audit-log-panel">
      {filter && (
        <div
          className="bg-[#FFF5EB] border border-[#E8DED3] text-[#2C2823] rounded-lg px-3 py-2 mb-4 flex items-center justify-between gap-3"
          data-testid="audit-log-filter-chip"
        >
          <p className="text-sm min-w-0 truncate">
            <span className="text-[#9B8F88]">Filtered to:</span>{' '}
            <span className="font-semibold">{filter.school_name}</span>
          </p>
          <button
            onClick={onClearFilter}
            className="text-xs text-[#E85D35] font-semibold hover:underline shrink-0"
            data-testid="audit-log-clear-filter"
          >
            ✕ Clear filter
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#6B6560]">
          {filter
            ? `${entries.length} edit${entries.length === 1 ? '' : 's'} for this school`
            : `Last ${entries.length} school edits — newest first.`}
        </p>
        <button
          onClick={fetchLog}
          disabled={loading}
          className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E8DED3] text-[#2C2823] hover:bg-[#FFF5EB]"
          data-testid="audit-log-refresh"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <p className="text-sm text-[#9B8F88]" data-testid="audit-log-empty">
          {filter
            ? 'No recorded edits for this school yet.'
            : 'No edits yet. Changes will appear here as soon as you save one.'}
        </p>
      )}

      <div className="space-y-3">
        {entries.map((e, i) => (
          <AuditLogEntry key={`${e.timestamp}-${i}`} entry={e} />
        ))}
      </div>
    </div>
  );
};

const AuditLogEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const schoolFields = Object.keys(entry.school_patch || {});
  const attrFields = Object.keys(entry.attr_patch || {});
  const totalFields = schoolFields.length + attrFields.length;

  return (
    <div
      className="bg-white border border-[#E8DED3] rounded-xl p-4"
      data-testid={`audit-entry-${entry.timestamp}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#2C2823] truncate">
            {entry.school_name || entry.school_id}
          </p>
          <p className="text-xs text-[#9B8F88] mt-0.5">
            {new Date(entry.timestamp).toLocaleString()} · {entry.actor} ·{' '}
            <span className="font-mono">{entry.school_id}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="bg-[#FFF5EB] text-[#E85D35] border border-[#E8DED3] px-2 py-0.5 rounded-full text-xs font-semibold">
            {totalFields} field{totalFields === 1 ? '' : 's'}
          </span>
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-xs text-[#E85D35] font-semibold hover:underline"
          >
            {open ? 'Hide' : 'Show'} diff
          </button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {schoolFields.map((f) => (
          <span
            key={`s-${f}`}
            className="bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded text-[10px] font-mono"
          >
            school.{f}
          </span>
        ))}
        {attrFields.map((f) => (
          <span
            key={`a-${f}`}
            className="bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded text-[10px] font-mono"
          >
            attr.{f}
          </span>
        ))}
      </div>

      {open && (
        <pre className="mt-3 bg-[#FEFDF8] border border-[#E8DED3] rounded-lg p-3 text-xs overflow-x-auto">
{JSON.stringify(
  { school: entry.school_patch, attributes: entry.attr_patch },
  null,
  2
)}
        </pre>
      )}
    </div>
  );
};

const EditorDrawer = ({
  editingId,
  detail,
  detailLoading,
  saving,
  saveMsg,
  onClose,
  onSave,
  onSchoolFieldChange,
  onAttrFieldChange,
}) => (
  <div className="fixed inset-0 z-40 flex" data-testid="school-editor-drawer">
    <div className="flex-1 bg-black/40" onClick={onClose} />
    <div className="w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-[#E8DED3] px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#2C2823]">Edit school</h2>
          <p className="text-xs text-[#9B8F88] font-mono">{editingId}</p>
        </div>
        <button
          onClick={onClose}
          className="text-[#9B8F88] hover:text-[#2C2823] text-sm"
          data-testid="school-editor-close"
        >
          ✕ Close
        </button>
      </div>

      {detailLoading && <p className="p-6 text-sm text-[#6B6560]">Loading…</p>}
      {!detailLoading && !detail && (
        <p className="p-6 text-sm text-red-600">{saveMsg || 'Could not load.'}</p>
      )}

      {detail && (
        <div className="p-6 space-y-6">
          <Section title="Basics">
            <LabeledInput
              label="Full name"
              value={detail.school.full_name}
              onChange={(v) => onSchoolFieldChange('full_name', v)}
              testId="school-field-full_name"
            />
            <LabeledInput
              label="Curriculum framework"
              value={detail.school.curriculum_framework}
              onChange={(v) => onSchoolFieldChange('curriculum_framework', v)}
              testId="school-field-curriculum_framework"
            />
            <div>
              <div className="text-xs font-semibold text-[#6B6560] mb-1">
                Campus locations (one per line)
              </div>
              <textarea
                value={detail.school.campus_locations}
                onChange={(e) => onSchoolFieldChange('campus_locations', e.target.value)}
                rows={3}
                className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 text-sm"
                data-testid="school-field-campus_locations"
              />
            </div>
            <LabeledInput
              label="Year groups covered (comma-separated integers)"
              value={detail.school.year_groups_covered}
              onChange={(v) => onSchoolFieldChange('year_groups_covered', v)}
              testId="school-field-year_groups_covered"
            />
            <LabeledInput
              label="COBIS tier"
              value={detail.school.cobis_tier}
              onChange={(v) => onSchoolFieldChange('cobis_tier', v)}
              testId="school-field-cobis_tier"
            />
            <label className="flex items-center gap-2 text-sm" data-testid="school-field-active">
              <input
                type="checkbox"
                checked={detail.school.active}
                onChange={(e) => onSchoolFieldChange('active', e.target.checked)}
              />
              Active (shown in matches)
            </label>
          </Section>

          <Section title="Verified flags">
            <div className="grid grid-cols-2 gap-2">
              {BOOL_FLAGS.map((f) => (
                <label
                  key={f.key}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`school-flag-${f.key}`}
                >
                  <input
                    type="checkbox"
                    checked={!!detail.school[f.key]}
                    onChange={(e) => onSchoolFieldChange(f.key, e.target.checked)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </Section>

          <Section title="Unique signal">
            <textarea
              value={detail.school.unique_signal}
              onChange={(e) => onSchoolFieldChange('unique_signal', e.target.value)}
              rows={4}
              className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 text-sm"
              data-testid="school-field-unique_signal"
            />
          </Section>

          <Section title="Annual fees (JSON: {year: amount_RM})">
            <textarea
              value={detail.school.annual_fees}
              onChange={(e) => onSchoolFieldChange('annual_fees', e.target.value)}
              rows={8}
              className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 text-xs font-mono"
              data-testid="school-field-annual_fees"
            />
          </Section>

          <Section title="Attributes — text">
            {ATTR_TEXT_FIELDS.map((f) =>
              f.long ? (
                <div key={f.key}>
                  <div className="text-xs font-semibold text-[#6B6560] mb-1">{f.label}</div>
                  <textarea
                    value={detail.attributes[f.key]}
                    onChange={(e) => onAttrFieldChange(f.key, e.target.value)}
                    rows={3}
                    className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 text-sm"
                    data-testid={`attr-field-${f.key}`}
                  />
                </div>
              ) : (
                <LabeledInput
                  key={f.key}
                  label={f.label}
                  value={detail.attributes[f.key]}
                  onChange={(v) => onAttrFieldChange(f.key, v)}
                  testId={`attr-field-${f.key}`}
                />
              )
            )}
          </Section>

          <Section title="Attributes — scores (0.0 – 1.0)">
            <div className="grid grid-cols-2 gap-3">
              {ATTR_NUMBER_FIELDS.map((f) => (
                <LabeledInput
                  key={f.key}
                  label={f.label}
                  type="number"
                  step={f.step}
                  value={detail.attributes[f.key]}
                  onChange={(v) => onAttrFieldChange(f.key, v)}
                  testId={`attr-field-${f.key}`}
                />
              ))}
            </div>
          </Section>

          <div className="sticky bottom-0 bg-white border-t border-[#E8DED3] -mx-6 px-6 py-4 flex items-center justify-between">
            <span className={`text-sm ${saveMsg === 'Saved.' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="school-editor-msg">
              {saveMsg}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-[#E8DED3] text-sm"
                data-testid="school-editor-cancel"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-[#E85D35] hover:bg-[#D94E26] disabled:opacity-60 text-white text-sm font-semibold"
                data-testid="school-editor-save"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h3 className="text-sm font-bold text-[#2C2823] uppercase tracking-wide mb-2">{title}</h3>
    <div className="space-y-3">{children}</div>
  </section>
);

const LabeledInput = ({ label, value, onChange, type = 'text', step, testId }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[#6B6560] mb-1 block">{label}</span>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#E8DED3] rounded-lg px-3 py-2 text-sm"
      data-testid={testId}
    />
  </label>
);

const AdminSchoolsPage = () => (
  <AdminShell
    title="Schools"
    subtitle="Edit school data, fees, flags and attributes"
  >
    <SchoolsBody />
  </AdminShell>
);

export default AdminSchoolsPage;
